<template>
  <div class="staff-subscriptions-page" data-testid="staff-subscriptions-page">
    <page-header
      :title="t('Assinaturas')"
      icon="pi pi-id-card"
    />

    <div class="filters-toolbar">
      <SelectButton
        v-model="viewMode"
        :options="viewOptions"
        option-label="label"
        option-value="value"
        :allow-empty="false"
        data-testid="staff-subscriptions-history-toggle"
      />

      <app-button
        :label="t('Emitir taxa')"
        icon="pi pi-dollar"
        severity="primary"
        size="small"
        data-testid="staff-subscriptions-emit-setup-fee"
        @click="openSetupFeeEmit"
      />

      <app-button
        :label="t('Processar vencidas')"
        icon="pi pi-play"
        severity="secondary"
        outlined
        size="small"
        :disabled="isSweeping"
        data-testid="staff-subscriptions-sweep"
        @click="askSweep"
      />


      <app-button
        :label="t('Histórico de rodadas')"
        icon="pi pi-history"
        severity="secondary"
        outlined
        size="small"
        data-testid="staff-subscriptions-sweep-runs"
        @click="openSweepRuns"
      />
    </div>

    <!--
      The round cuts real subscriptions, so the first click ASKS. Same inline-box
      confirmation the detail modal uses for its single cut — one pattern for both
      irreversible actions on this screen.
    -->
    <box
      v-if="confirmingSweep"
      type="danger"
      icon="pi pi-exclamation-triangle"
      :title="t('Processar assinaturas vencidas')"
      elem-id="boxStaffSubscriptionsSweep"
    >
      <div data-testid="staff-subscriptions-sweep-confirm">
        <p class="confirm-text">
          {{ t('A rodada corta todas as assinaturas cujo prazo de carência já venceu. O cancelamento é feito primeiro no gateway e não há reativação.') }}
        </p>
        <div class="actions-section">
          <app-button
            :label="t('Executar rodada')"
            icon="pi pi-play"
            severity="danger"
            size="small"
            :disabled="isSweeping"
            data-testid="staff-subscriptions-sweep-confirm-run"
            @click="handleSweep"
          />
          <app-button
            :label="t('Voltar')"
            severity="secondary"
            outlined
            size="small"
            :disabled="isSweeping"
            data-testid="staff-subscriptions-sweep-abort"
            @click="confirmingSweep = false"
          />
        </div>
      </div>
    </box>

    <StaffSubscriptionsTable
      :rows="displayRows"
      @open-detail="openDetailModal"
      @extend="openExtendModal"
      @refresh="handleRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import PageHeader from '../../../ui/PageHeader.vue'
import SelectButton from '../../../ui/SelectButton.vue'
import Box from '../../../ui/Box.vue'
import AppButton from '../../../ui/AppButton.vue'
import StaffSubscriptionsTable from './StaffSubscriptionsTable.vue'
import type { Subscriptions_AdminSubscriptionRow } from '../../../contracts/Subscriptions_AdminSubscriptionRow'

const { t } = useI18n()
const { adminRows, listAll, runSweep } = useSubscriptionAdmin()
const { formatDate, fromApiDateToDate } = useDateFormatter()

type ViewMode = 'live' | 'all'

const viewMode = ref<ViewMode>('live')
const isSweeping = ref(false)
const confirmingSweep = ref(false)

const viewOptions = [
  { label: t('Ao vivo'), value: 'live' },
  { label: t('Histórico'), value: 'all' },
]

const displayRows = computed(() => {
  let result = adminRows.value

  if (viewMode.value === 'live') {
    result = result.filter((r) => r.is_live === true)
  }

  return result.map((r) => {
    const rawDate = r.current_period_end ?? r.grace_until ?? null
    const d = rawDate ? fromApiDateToDate(rawDate) : null
    return {
      ...r,
      fim_carencia_display: d ? formatDate(d) : '',
    }
  })
})

/**
 * M3d: clicking the subscription id opens its detail modal — the one house for
 * Estender and Cancelar. The row is handed over whole; the modal never refetches.
 */
function openDetailModal(row: Subscriptions_AdminSubscriptionRow): void {
  eventBus.emit('modal.subscriptionDetail.open', { staff: true, row })
}

/**
 * Still reachable straight from the table for the ONE row shape with no detail
 * modal behind it: a franchise with no subscription at all (subscription_id
 * null), where Estender means "grant a grace window". See StaffSubscriptionsTable.
 */
function openExtendModal(row: Subscriptions_AdminSubscriptionRow): void {
  eventBus.emit('modal.staffSubscriptionsExtend.open', { row })
}

/**
 * The run log is a separate house: the round REPORTS through a toast that is
 * gone a moment later, and the log is where "o que a rodada de ontem fez"
 * actually lives. It reads on open, so the round the staff just triggered is
 * already in it.
 */
function openSweepRuns(): void {
  eventBus.emit('modal.staffSweepRuns.open')
}

// ── The manual round ──────────────────────────────────────────────────────────

function askSweep(): void {
  confirmingSweep.value = true
}

/**
 * The round REPORTS, it does not refuse: every counter it returns describes a
 * round that ran, so the whole result is a success envelope. Two of those
 * results still must not read as "all good":
 *
 *   circuit_open — the breaker refused to open the round at all. Nothing was
 *   cut and nothing failed; the counters are all zero because the mechanism
 *   deliberately stood down. That is a WARNING to read, never a red error.
 *
 *   failed_count > 0 — the round ran but some rows did not survive it. The
 *   summary still shows, in the same warning tone, because the other counters
 *   are real and the staff needs them.
 *
 * A thrown error here means the round never ran (auth, or an exception) — the
 * composable checked the ENVELOPE, since HTTP is 200 either way.
 */
async function handleSweep(): Promise<void> {
  isSweeping.value = true
  try {
    const dados = await runSweep()
    log('StaffSubscriptionsPage', 'handleSweep', 'Rodada executada', dados)
    confirmingSweep.value = false

    if (dados.circuit_open === true) {
      // The breaker trips PRE-FLIGHT, when the due rows outnumber
      // max(10, 10% of the live roster) — so candidate_count is the number that
      // matters here, and it is the only one that is non-zero.
      eventBus.emit(
        'globalMessage.warning',
        `${t('O disjuntor bloqueou a rodada')}: ${dados.candidate_count} ${t('assinaturas venceram de uma vez, acima do limite de segurança. Nada foi cancelado — não é um erro, é o mecanismo pedindo que alguém olhe antes de cortar.')}`,
      )
    } else {
      // The counters are concatenated OUTSIDE t(). The app's translation lookup
      // returns unregistered keys VERBATIM, so a '{cancelled}' placeholder would
      // reach the staff un-interpolated — the same production bug the so-search
      // tooltip once shipped. Label:value pairs also survive translation without
      // assuming Portuguese word order.
      const summary = [
        `${t('vencidas')}: ${dados.candidate_count}`,
        `${t('cortadas')}: ${dados.cancelled_count}`,
        `${t('adiadas')}: ${dados.deferred_count}`,
        `${t('sem gateway')}: ${dados.refused_count}`,
        `${t('com falha')}: ${dados.failed_count}`,
      ].join(' · ')
      eventBus.emit(
        dados.failed_count > 0 ? 'globalMessage.warning' : 'globalMessage.success',
        `${t('Rodada concluída')} — ${summary}`,
      )
    }

    await handleRefresh()
  } catch (err) {
    const reason = (err as Error)?.message ?? ''
    log('StaffSubscriptionsPage', 'handleSweep', 'Rodada não executada', { reason })
    eventBus.emit('globalMessage.error', `${t('A rodada não foi executada')}: ${reason}`)
    confirmingSweep.value = false
  } finally {
    isSweeping.value = false
  }
}

function openSetupFeeEmit(): void {
  eventBus.emit('modal.setup-fee-emit.open')
}

async function handleRefresh(): Promise<void> {
  try {
    await listAll()
  } catch (err) {
    log('StaffSubscriptionsPage', 'handleRefresh', 'Erro ao recarregar assinaturas', err)
  }
}


onMounted(async () => {
  try {
    await listAll()
  } catch (err) {
    log('StaffSubscriptionsPage', 'onMounted', 'Erro ao carregar assinaturas', err)
  }
  eventBus.on('staffSubscriptions.refresh', handleRefresh)
})

onBeforeUnmount(() => {
  eventBus.off('staffSubscriptions.refresh', handleRefresh)
})
</script>

<style scoped>
.staff-subscriptions-page {
  padding: 24px;
}

.filters-toolbar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.actions-section {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.confirm-text {
  margin: 0 0 0.75rem;
}
</style>
